import { updateReleaseNotifications } from 'api/profiles'
import { Icon } from 'components/common/Icon'
import { Modal } from 'components/common/Modal'
import { Option } from 'components/common/Option'
import { Select } from 'components/common/Select'
import { Separator } from 'components/common/Separator'
import {
  calendarReminderOptions,
  calendarSecondReminderOptions,
} from 'constants/calendar'
import routes from 'constants/routes'
import { useAuth, useOverlay } from 'contexts'
import dayjs from 'dayjs'
import { Field, Form, Formik, FormikProps } from 'formik'
import { useUserProfile } from 'hooks'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef } from 'react'

type FormInputs = {
  requiredReminder: string
  optionalReminder: string
}

type Props = {
  imageSrc?: string
  imageAlt?: string
}

export const SavedReleaseModal = ({ imageSrc, imageAlt }: Props) => {
  const { t } = useTranslation('release_calendar')
  const { data: userProfile } = useUserProfile()
  const { user } = useAuth()
  const { setIsOpen } = useOverlay()

  const formikRef = useRef<FormikProps<FormInputs>>(null)

  useEffect(() => {
    const formikCurrent = formikRef.current

    return () => {
      formikCurrent?.submitForm()
    }
  }, [formikRef])

  const getReminderValue = ({
    profileValue,
    allValues,
  }: {
    profileValue?: number
    allValues: number[]
  }) => {
    if (!profileValue) return String(allValues[0])

    if (!allValues.includes(profileValue)) return String(allValues[0])

    return String(profileValue)
  }

  return (
    <Modal
      className='max-w-[37.5rem]'
      header={
        <div className='flex items-center space-x-2'>
          <Icon
            name='filled-success'
            filled
            size={24}
            className='text-success-default'
          />

          <h5 className='font-body text-xl font-medium tracking-normal'>
            <span className='hidden sm:block'>
              {t('added_to_saved_releases')}
            </span>
            <span className='sm:hidden'>{t('release_saved')}</span>
          </h5>
        </div>
      }
      body={
        <div>
          <div className='flex flex-col items-center space-y-4 sm:flex-row sm:space-x-5 sm:space-y-0'>
            <div className='flex aspect-1 w-full max-w-[10.25rem] items-center justify-center bg-warm-50'>
              {imageSrc && imageAlt && (
                <Image
                  src={imageSrc}
                  alt={imageAlt}
                  width={124}
                  height={124}
                  className='object-contain'
                />
              )}
            </div>

            <div className='space-y-4 text-sm text-cool-600'>
              <p>{t('save_upcoming_releases_to_get_email')}</p>

              <p>
                <Trans
                  i18nKey='release_calendar:see_all_your_saved_releases'
                  components={{
                    b: (
                      <Link
                        href={`${routes.releaseCalendar.base}?tab=saved`}
                        onClick={() => setIsOpen(false)}
                        className='text-secondary-default'
                      ></Link>
                    ),
                  }}
                />
              </p>
            </div>
          </div>

          <Separator className='my-4 sm:my-5' color='cool-300' />

          <Formik
            innerRef={formikRef}
            initialValues={
              {
                requiredReminder: getReminderValue({
                  allValues: calendarReminderOptions,
                  profileValue: userProfile?.releaseSettings?.notification1,
                }),
                optionalReminder: getReminderValue({
                  allValues: calendarSecondReminderOptions,
                  profileValue: userProfile?.releaseSettings?.notification2,
                }),
              } as FormInputs
            }
            enableReinitialize
            onSubmit={async ({ optionalReminder, requiredReminder }) => {
              await updateReleaseNotifications({
                timeZone:
                  userProfile?.releaseSettings?.timeZone || dayjs.tz.guess(),
                notification1: Number(requiredReminder),
                notification2: Number(optionalReminder),
              })
            }}
          >
            {({
              values: { optionalReminder, requiredReminder },
              setFieldValue,
            }) => (
              <Form>
                <Field
                  as={Select}
                  id='email-required-reminder'
                  value={requiredReminder}
                  onChange={(value: string) =>
                    setFieldValue('requiredReminder', value)
                  }
                  label={t('email_reminders')}
                  caption={
                    <Trans
                      i18nKey='release_calendar:sent_to_email'
                      values={{ email: user?.email }}
                      components={{ em: <em /> }}
                    />
                  }
                  pill
                  variant='gray-bg'
                >
                  {calendarReminderOptions.map(value => (
                    <Option key={value} value={String(value)}>
                      {value < 60
                        ? t('x_minutes_before', { x: value })
                        : t('one_hour_before')}
                    </Option>
                  ))}
                </Field>

                <Field
                  as={Select}
                  id='email-optional-reminder'
                  value={optionalReminder}
                  onChange={(value: string) =>
                    setFieldValue('optionalReminder', value)
                  }
                  pill
                  variant='gray-bg'
                  className='mt-3 w-full'
                  caption={t('add_a_second_reminder')}
                >
                  {calendarSecondReminderOptions.map(value => (
                    <Option key={value} value={String(value)}>
                      {(() => {
                        if (!value) return t('no_reminder')

                        if (value === 60) return t('one_hour_before')

                        return t('x_minutes_before', { x: value })
                      })()}
                    </Option>
                  ))}
                </Field>
              </Form>
            )}
          </Formik>
        </div>
      }
    />
  )
}
